import React from 'react';
import { useTranslation } from 'react-i18next';

export type RemarkAppendFieldsProps = {
  currentRemark?: string | null;
  appendValue: string;
  onAppendChange: (value: string) => void;
  /** When true, marks the append textarea as required */
  required?: boolean;
  disabled?: boolean;
  id?: string;
  currentLabel?: string;
  appendLabel?: string;
  emptyCurrentLabel?: string;
  placeholder?: string;
  rows?: number;
  className?: string;
  error?: string | null;
};

/**
 * Read-only current remark + append-only textarea for entity edit forms.
 */
export const RemarkAppendFields: React.FC<RemarkAppendFieldsProps> = ({
  currentRemark,
  appendValue,
  onAppendChange,
  required = false,
  disabled = false,
  id = 'remark-append',
  currentLabel,
  appendLabel,
  emptyCurrentLabel,
  placeholder,
  rows = 3,
  className = '',
  error,
}) => {
  const { t } = useTranslation();
  const current = String(currentRemark ?? '').trim();
  const resolvedCurrentLabel = currentLabel ?? t('common.remarkAppend.currentLabel');
  const resolvedAppendLabel = appendLabel ?? t('common.remarkAppend.appendLabel');
  const resolvedEmpty = emptyCurrentLabel ?? t('common.remarkAppend.emptyCurrent');
  const resolvedPlaceholder = placeholder ?? t('common.remarkAppend.placeholder');

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
        {resolvedAppendLabel}
        {required ? <span className="text-red-500 ml-0.5">*</span> : null}
      </label>
      <p className="mb-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 whitespace-pre-wrap">
        <span className="block text-xs font-medium text-gray-500 mb-1">{resolvedCurrentLabel}</span>
        {current || resolvedEmpty}
      </p>
      <textarea
        id={id}
        value={appendValue}
        onChange={(e) => onAppendChange(e.target.value)}
        rows={rows}
        disabled={disabled}
        required={required}
        className={`w-full rounded-md border px-3 py-2 text-sm shadow-xs focus:border-primary focus:ring-primary disabled:bg-gray-100 ${
          error ? 'border-red-300' : 'border-gray-300'
        }`}
        placeholder={resolvedPlaceholder}
      />
      {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
    </div>
  );
};
